import { memo, useState, useCallback, useMemo } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { wrap } from '@popmotion/popcorn'
import { useTranslation } from 'services/Translation'
import Dummy from 'components/common/widgets/Dummy'
import { usePatientRouteParams } from 'components/patient/Parameters'
import { findBodyPartParent } from 'components/common/widgets/Dummy/Dummy.tools'
import NavArrowIcon from 'assets/icons/arrows/Paginate'
import BODYPARTS_LABELS from 'components/common/widgets/Dummy/constants/bodyparts.labels'

import { ImageContainer } from './Styles'
import texts from './Texts'
import { imageVariants } from './Animations'

// Extract body parts from CS model answers (same logic as old formatSummary)
const extractBodyParts = (answers) => {
  if (!answers?.length) return []
  const bodyParts = []
  const localizationAnswers = answers.filter(a => a.question?.template === 'Localization')
  localizationAnswers.forEach(({ response, question }) => {
    if (!response) return
    let responseArray
    if (response.charAt(0) === '[') {
      try { responseArray = JSON.parse(response) } catch { responseArray = null }
    } else if (response.includes(', ')) {
      responseArray = response.split(', ')
    }
    if (responseArray && responseArray.length > 0) {
      const bodyPartsByParent = {}
      responseArray.forEach(res => {
        const parentResult = findBodyPartParent(res)
        const parent = parentResult ? parentResult.name : res
        if (!bodyPartsByParent[parent]) bodyPartsByParent[parent] = [res]
        else bodyPartsByParent[parent].push(res)
      })
      Object.keys(bodyPartsByParent).forEach(bp => {
        bodyParts.push({ name: question.text, parts: bodyPartsByParent[bp] })
      })
    } else {
      bodyParts.push({ name: question.text, parts: response })
    }
  })
  return bodyParts
}

const DummySideBar = () => {
  const { t, lang } = useTranslation(texts)

  // Single source of truth: CS.useRegistration via usePatientRouteParams
  const { summary } = usePatientRouteParams()

  const [[page, direction], setPage] = useState([0, 0])

  // Extract body parts from CS model answers
  const bodyParts = useMemo(
    () => extractBodyParts(summary?.answers)?.filter(x => x.parts !== 'null') || [],
    [summary?.answers]
  )

  const imageIndex = wrap(0, bodyParts.length, page)

  const paginate = useCallback((newDirection) => {
    setPage(([prev]) => [prev + newDirection, newDirection])
  }, [])

  if (!summary) return null

  return (
    <>
      <div className="mt-16 w-3/5 border-b border-grey-100 mx-auto pb-3 text-center font-csc65 text-lg">
        {t('title')}
      </div>
      <div className="flex flex-col justify-center">
        {!!bodyParts.length && (
          <>
            <ImageContainer>
              <AnimatePresence initial={false} custom={direction}>
                <motion.div
                  style={{
                    position: 'absolute',
                    height: '100%',
                    width: '100%',
                    top: '50%',
                  }}
                  key={page}
                  custom={direction}
                  variants={imageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: 'spring', stiffness: 300, damping: 200 },
                    opacity: { duration: 0.2 },
                  }}
                >
                  {bodyParts[imageIndex] && (
                    <Dummy
                      selection={
                        Array.isArray(bodyParts[imageIndex].parts)
                          ? bodyParts[imageIndex].parts
                          : bodyParts[imageIndex].parts.includes(', ')
                            ? bodyParts[imageIndex].parts.split(', ')
                            : bodyParts[imageIndex].parts
                      }
                      questionId={bodyParts[imageIndex].name}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </ImageContainer>
            {bodyParts[imageIndex] && (
              <div className="text-center mt-6 px-4">
                <div
                  className="inline-block bg-white border border-grey-100 rounded-lg px-4 py-2 shadow-sm"
                  style={{
                    borderColor: '#EB7C0C',
                    borderWidth: '1px',
                    backgroundColor: '#EB7C0C08',
                  }}
                >
                  <div className="font-csc65 text-sm" style={{ color: '#9B2A3B' }}>
                    {t('selectedArea')}
                  </div>
                  <div className="font-csc45 text-base mt-1" style={{ color: '#1C165B' }}>
                    {Array.isArray(bodyParts[imageIndex].parts)
                      ? bodyParts[imageIndex].parts
                          .map(part => BODYPARTS_LABELS?.[lang]?.[part] || part)
                          .join(', ')
                      : bodyParts[imageIndex].parts.includes(', ')
                        ? bodyParts[imageIndex].parts
                            .split(', ')
                            .map(part => BODYPARTS_LABELS?.[lang]?.[part] || part)
                            .join(', ')
                        : BODYPARTS_LABELS?.[lang]?.[bodyParts[imageIndex].parts] ||
                          bodyParts[imageIndex].parts}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {bodyParts.length > 1 && (
        <div className="flex flex-col items-center w-full justify-center">
          <div className="font-csc45 text-sm mb-2" style={{ color: '#9B2A3B' }}>
            {t('pageIndicator')
              .replace('{current}', imageIndex + 1)
              .replace('{total}', bodyParts.length)}
          </div>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => paginate(-1)}
              style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer' }}
              aria-label="Previous body part"
            >
              <NavArrowIcon style={{ transform: 'rotate(180deg)' }} />
            </button>
            <div className="mx-6 w-px h-4 bg-grey-100" />
            <button
              type="button"
              onClick={() => paginate(1)}
              style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer' }}
              aria-label="Next body part"
            >
              <NavArrowIcon />
            </button>
          </div>
        </div>
      )}
    </>
  )
}

DummySideBar.whyDidYouRender = true

export default memo(DummySideBar)
